import React, { useRef, useMemo } from 'react';
import { theme } from '../../../theme';
import type { TimeBin } from './KalenderwocheBarChart';
import type { PeriodResidueBin, PrimaryAxisVolumeBin } from '../../../services/api';
import { useEChartsLifecycle, STD_TOOLTIP_STYLE, makeCountTooltipFormatter } from './_chartCommon';
import { useCategoryMeta } from '../../../context/ClassificationSchemaContext';
import { classifiedBinTotals, residueByBinLabel, barLabelText, RESIDUE_LABEL_RICH, withResidueBins, residueTooltipLines } from './_volumeStack';

// <purpose>KW.Verlauf — stepped area, count labels above each step.</purpose>
// <constraints>
//   part 7 round 5, 2026-10-14 (operator-accepted screen): the one Verlauf variant that prints a per-bin total, so it
//   speaks the Balken language — the step is the classified total (classifiedBinTotals); a bin with residue
//   (stats.perPeriodResidue, by binLabel) gets the label's second small line „+f +w". Absent list ⇒ no „+n".
// </constraints>

type Props = {
  data: TimeBin[];
  rawData?: PrimaryAxisVolumeBin[];
  residue?: PeriodResidueBin[];
  filterHash?: string;
};

export const KwVerlauf: React.FC<Props> = ({ data: servedData, rawData, residue, filterHash }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // The axis: classified bins UNION residue bins (a residue-only period stands at 0 with its „+n").
  const data = useMemo(() => withResidueBins(servedData, residue), [servedData, residue]);
  const NAVY = theme.colors.navy;
  const cm = useCategoryMeta();
  const residueMap = useMemo(() => residueByBinLabel(residue), [residue]);

  useEChartsLifecycle(containerRef, chart => {
    const values = classifiedBinTotals(data, rawData, cm);
    chart.setOption({
      grid: { left: 36, right: 16, top: 38, bottom: 26 }, // top room for a two-line residue label (as KwBalken)
      tooltip: {
        ...STD_TOOLTIP_STYLE,
        trigger: 'axis',
        axisPointer: { type: 'line', lineStyle: { color: NAVY, opacity: 0.35, width: 1 } },
        // The count line, then the bin's residue words with their counts (one helper with Tag.Balken).
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          return makeCountTooltipFormatter(NAVY)(params) + residueTooltipLines(residueMap?.get(data[p?.dataIndex]?.label), cm);
        },
      },
      xAxis: {
        type: 'category',
        data: data.map(d => d.label),
        axisLine: { lineStyle: { color: '#dde0e8' } },
        axisTick: { show: false },
        axisLabel: { color: '#9095a3', fontSize: 9, interval: data.length > 16 ? 1 : 0 },
      },
      yAxis: {
        type: 'value',
        splitLine: { lineStyle: { color: '#f1f2f6' } },
        axisLabel: { color: '#9095a3', fontSize: 9 },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [{
        type: 'line',
        data: values,
        step: 'middle',
        symbol: 'circle',
        symbolSize: 5,
        itemStyle: { color: 'white', borderColor: NAVY, borderWidth: 1.5 },
        lineStyle: { color: NAVY, width: 1.5 },
        areaStyle: {
          color: {
            type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(6,16,90,0.22)' },
              { offset: 1, color: 'rgba(6,16,90,0.01)' },
            ],
          },
        },
        emphasis: {
          scale: 1.6,
          itemStyle: { color: NAVY, borderColor: 'white', borderWidth: 2, shadowColor: 'rgba(6,16,90,0.45)', shadowBlur: 10 },
        },
        label: {
          show: true, position: 'top', color: NAVY, fontSize: 10, fontWeight: 600,
          formatter: (p: any) => barLabelText(values[p.dataIndex] ?? 0, residueMap?.get(data[p.dataIndex]?.label)),
          rich: RESIDUE_LABEL_RICH,
        },
      }],
    });
  }, [data, rawData, residueMap, filterHash, cm]);

  return <div ref={containerRef} style={{ width: '100%', height: 220 }} />;
};
